import { useEffect, useState } from 'react';
import decorPreview from '@/assets/decor-preview.png';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { HsvColorPicker } from '@/components/onboarding/steps/HsvColorPicker';
import { X } from 'lucide-react';
import { useIsMobile } from '@/hooks/useMobile';

interface DecorColorPickerProps {
  color: string;
  onColorChange: (color: string) => void;
  onContinue: () => void;
  onBack: () => void;
}

export function DecorColorPicker({
  color,
  onColorChange,
  onContinue,
  onBack,
}: Readonly<DecorColorPickerProps>) {
  const [selectedColor, setSelectedColor] = useState<string>(color);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setSelectedColor(color);
  }, [color]);

  const handlePickerChange = (hex: string) => {
    setSelectedColor(hex);
    onColorChange(hex);
  };

  // Desktop: Show only the color picker (preview is in left panel)
  if (!isMobile) {
    return (
      <div className="w-full max-w-sm">
        <HsvColorPicker 
          value={selectedColor} 
          onChange={handlePickerChange}
        />
      </div>
    );
  }

  // Mobile: Show preview + drawer
  return (
    <div className="h-full flex flex-col">
      {/* Preview container with color input inside */}
      <div className="flex-1 flex items-center justify-center min-h-0">
        <div className="flex flex-col items-center gap-4 w-56">
          {/* Image preview */}
          <div className="relative aspect-[9/16] max-h-[50vh] w-full rounded-xl overflow-hidden">
            {/* Base dark layer */}
            <div className="absolute inset-0 bg-[#0a0a12]" />
            
            {/* Radial halo effect - main color glow */}
            {selectedColor && (
              <div 
                className="absolute inset-0 transition-all duration-300"
                style={{
                  background: `
                    radial-gradient(
                      ellipse 120% 90% at 50% 40%,
                      ${selectedColor} 0%,
                      ${selectedColor}cc 30%,
                      ${selectedColor}66 55%,
                      ${selectedColor}33 75%,
                      transparent 100%
                    )
                  `,
                }}
              />
            )}
            
            {/* Vignette effect for depth */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `
                  radial-gradient(
                    ellipse 100% 100% at 50% 50%,
                    transparent 60%,
                    rgba(0, 0, 0, 0.1) 85%,
                    rgba(0, 0, 0, 0.2) 100%
                  )
                `,
              }}
            />
            
            {/* Subtle noise texture overlay */}
            <div 
              className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              }}
            />
            
            {/* PNG overlay image */}
            <img 
              src={decorPreview} 
              alt="Aperçu du décor"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          </div>

          {/* Color input area */}
          <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <div className="flex items-stretch gap-3 w-full">
              {/* Color swatch */}
              <DrawerTrigger asChild>
                <button
                  className="w-8 h-8 rounded-lg border-2 border-white/20 overflow-hidden relative"
                  style={{ backgroundColor: selectedColor }}
                />
              </DrawerTrigger>
              
              {/* Select button */}
              <DrawerTrigger asChild>
                <button className="flex-1 h-8 rounded-lg bg-white/10 hover:bg-white/15 transition-colors text-white text-sm font-medium">
                  Sélectionner
                </button>
              </DrawerTrigger>
            </div>

            <DrawerContent 
              className="bg-background border-border" 
              showHandle={false}
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
            >
              <div 
                className="px-6 py-6"
                onPointerDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              >
                <HsvColorPicker 
                  value={selectedColor} 
                  onChange={handlePickerChange}
                  closeButton={
                    <DrawerClose className="rounded-full p-1.5 hover:bg-muted transition-colors">
                      <X className="h-4 w-4 text-muted-foreground" />
                    </DrawerClose>
                  }
                />
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    </div>
  );
}
